import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { visitorService } from '../../../services/visitorService';

const VisitorRegistrationModal = ({ onSubmit, onClose, galleryTitle, galleryId }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData?.name?.trim()?.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-()]{10,}$/?.test(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitError(null);
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      await visitorService?.registerVisitor(galleryId, formData);
      onSubmit(formData);
    } catch (error) {
      setSubmitError(error?.message || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/80">
      <div className="w-full max-w-md bg-card rounded-2xl border border-border shadow-warm-2xl overflow-hidden">
        <div className="p-6 md:p-8 border-b border-border">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="UserPlus" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold">
                  Register to View
                </h2>
                <p className="text-sm text-muted-foreground">
                  Access gallery content
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-muted/50 border border-border">
            <p className="text-sm">
              <span className="font-medium">Gallery:</span> {galleryTitle}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-6">
          {submitError && (
            <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/20">
              <div className="flex gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{submitError}</p>
              </div>
            </div>
          )}

          <Input
            label="Full Name"
            type="text"
            name="name"
            placeholder="Enter your full name"
            value={formData?.name}
            onChange={handleChange}
            error={errors?.name}
            required
          />

          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="your.email@example.com"
            value={formData?.email}
            onChange={handleChange}
            error={errors?.email}
            required
            description="We'll send gallery updates to this email"
          />

          <Input
            label="Phone Number"
            type="tel"
            name="phone"
            placeholder="+1 (555) 000-0000"
            value={formData?.phone}
            onChange={handleChange}
            error={errors?.phone}
            required
            description="Include country code for international numbers"
          />

          <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
            <div className="flex gap-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div className="text-xs md:text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">
                  Privacy Notice
                </p>
                <p>
                  Your information will be used solely for gallery access tracking and communication. We respect your privacy and will not share your details with third parties.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isSubmitting}
              iconName="ArrowRight"
              iconPosition="right"
              className="flex-1"
            >
              Continue to Gallery
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VisitorRegistrationModal;